import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Self-service password change. Doubles as the FORCED screen when the admin has flagged
// must_change_password — in that mode the user can't navigate away until it's done.
export default function ChangePassword() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.must_change_password;

  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Who reset this password, and when (option 2, 15 Oct 2026). If the user did not ask for it, they now know.
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!forced) return;
    api.get('/auth/password-reset-notice').then(r => setNotice(r.data.data || null)).catch(() => setNotice(null));
  }, [forced]);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (form.new_password.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (form.new_password !== form.confirm) { setError('New password and confirmation do not match.'); return; }
    if (form.new_password === form.current_password) { setError('New password must be different from the current one.'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      updateUser({ must_change_password: false });   // clear the gate so routing unblocks
      setDone(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change password.');
    } finally { setSaving(false); }
  };

  const input = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const label = { fontSize: 11, color: '#888', fontWeight: 700, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f8', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 14, width: 420, maxWidth: '100%', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ background: '#2D2C72', padding: '18px 24px' }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>{forced ? 'Set a New Password' : 'Change Password'}</h2>
          <div style={{ fontSize: 12, color: '#C7D2FE', marginTop: 3 }}>{user?.email}</div>
        </div>
        <div style={{ padding: 24 }}>
          {forced && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
              🔒 Your administrator requires you to change your password before continuing.
            </div>
          )}
          {forced && notice && (
            <div role="status" style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13 }}>
              Your password was {notice.action === 'user_force_change' ? 'flagged for a change' : 'reset'} by <b>{notice.by_name || 'an administrator'}</b> on {new Date(notice.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}. If you did not ask for this, tell Rajan before you continue.
            </div>
          )}
          {done ? (
            <div style={{ background: '#EAF7EE', color: '#166534', borderRadius: 8, padding: '14px', fontSize: 14, fontWeight: 600, textAlign: 'center' }}>✓ Password changed. Redirecting…</div>
          ) : (
            <form onSubmit={submit}>
              {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '9px 12px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
              <div style={{ marginBottom: 14 }}><label style={label}>Current Password</label>
                <input style={input} type="password" required autoComplete="current-password" value={form.current_password} onChange={(e) => set('current_password', e.target.value)} /></div>
              <div style={{ marginBottom: 14 }}><label style={label}>New Password (min 8 chars)</label>
                <input style={input} type="password" required minLength={8} autoComplete="new-password" value={form.new_password} onChange={(e) => set('new_password', e.target.value)} /></div>
              <div style={{ marginBottom: 18 }}><label style={label}>Confirm New Password</label>
                <input style={input} type="password" required autoComplete="new-password" value={form.confirm} onChange={(e) => set('confirm', e.target.value)} /></div>
              <button type="submit" disabled={saving} style={{ width: '100%', padding: '11px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Changing…' : 'Change Password'}</button>
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                {forced
                  ? <button type="button" onClick={async () => { await logout(); navigate('/login'); }} style={{ background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Log out instead</button>
                  : <button type="button" onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Cancel</button>}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
